import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import projectsData from '../../data/projects.json';
import ImageUploader from './ImageUploader';

const ProjectsAdmin = () => {
  const [projects, setProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState({
    id: null,
    title: '',
    description: '',
    github: '',
    image: 'https://picsum.photos/400/200',
    demo: '',
    category: 'web',
    technologies: []
  });
  const [isAdding, setIsAdding] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Common categories
  const categories = ['web', 'featured', 'mobile', 'design'];
  
  // Common technologies
  const commonTechs = ['React', 'HTML5', 'CSS3', 'JavaScript', 'TypeScript', 'Node.js', 'MongoDB', 'Express', 'Next.js', 'Styled Components'];

  useEffect(() => {
    // Load projects from the JSON file
    setProjects(projectsData);
  }, []);

  const handleSaveProjects = () => {
    // In a real implementation, this would save to the server
    // For now, we'll just show a success message
    setSuccessMessage('Projects saved successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    
    console.log('Projects to save:', projects);
    
    // You would typically use an API call here:
    // fetch('/api/projects', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(projects)
    // })
  };

  const handleEditProject = (project) => {
    setEditingProject({...project});
  };

  const handleDeleteProject = (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      setProjects(projects.filter(project => project.id !== id));
    }
  };

  const handleAddProject = () => {
    const newId = Math.max(0, ...projects.map(p => p.id)) + 1;
    const projectToAdd = { ...newProject, id: newId };
    setProjects([...projects, projectToAdd]);
    setIsAdding(false);
    setNewProject({
      id: null,
      title: '',
      description: '',
      github: '',
      image: 'https://picsum.photos/400/200',
      demo: '',
      category: 'web',
      technologies: []
    });
  };

  const handleUpdateProject = () => {
    setProjects(projects.map(p => p.id === editingProject.id ? editingProject : p));
    setEditingProject(null);
  };

  const handleTechnologyToggle = (tech, project) => {
    const updatedTech = project.technologies.includes(tech)
      ? project.technologies.filter(t => t !== tech)
      : [...project.technologies, tech];
    
    if (project === editingProject) {
      setEditingProject({...project, technologies: updatedTech});
    } else {
      setNewProject({...project, technologies: updatedTech});
    }
  };

  const handleImageUpload = (url, project) => {
    if (project === editingProject) {
      setEditingProject({...project, image: url});
    } else {
      setNewProject({...project, image: url});
    }
  };

  return (
    <Container>
      <Header>
        <h2>Manage Projects</h2>
        <AddButton onClick={() => setIsAdding(true)}>
          <FaPlus /> Add New Project
        </AddButton>
      </Header>

      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

      {isAdding && (
        <FormContainer>
          <h3>Add New Project</h3>
          <FormGroup>
            <label>Title</label>
            <input
              type="text"
              value={newProject.title}
              onChange={(e) => setNewProject({...newProject, title: e.target.value})}
              placeholder="Project Title"
            />
          </FormGroup>
          
          <FormGroup>
            <label>Description</label>
            <textarea
              value={newProject.description}
              onChange={(e) => setNewProject({...newProject, description: e.target.value})}
              placeholder="Project Description"
              rows="4"
            />
          </FormGroup>
          
          <FormRow>
            <FormGroup>
              <label>GitHub Link</label>
              <input
                type="text"
                value={newProject.github}
                onChange={(e) => setNewProject({...newProject, github: e.target.value})}
                placeholder="GitHub URL"
              />
            </FormGroup>
            
            <FormGroup>
              <label>Demo Link</label>
              <input
                type="text"
                value={newProject.demo}
                onChange={(e) => setNewProject({...newProject, demo: e.target.value})}
                placeholder="Demo URL"
              />
            </FormGroup>
          </FormRow>
          
          <FormGroup>
            <label>Category</label>
            <select 
              value={newProject.category}
              onChange={(e) => setNewProject({...newProject, category: e.target.value})}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </FormGroup>
          
          <FormGroup>
            <label>Technologies</label>
            <TechContainer>
              {commonTechs.map(tech => (
                <TechBadge 
                  key={tech} 
                  $active={newProject.technologies.includes(tech)}
                  onClick={() => handleTechnologyToggle(tech, newProject)}
                >
                  {tech}
                </TechBadge>
              ))}
              {/* Custom tech input could be added here */}
            </TechContainer>
          </FormGroup>
          
          <FormGroup>
            <label>Project Image</label>
            <ProjectImagePreview>
              {newProject.image && <img src={newProject.image} alt="Project preview" />}
            </ProjectImagePreview>
            <ImageUploader onImageUpload={(url) => handleImageUpload(url, newProject)} />
          </FormGroup>
          
          <ButtonGroup>
            <SaveButton onClick={handleAddProject}>Add Project</SaveButton>
            <CancelButton onClick={() => setIsAdding(false)}>Cancel</CancelButton>
          </ButtonGroup>
        </FormContainer>
      )}

      {editingProject && (
        <FormContainer>
          <h3>Edit Project</h3>
          <FormGroup>
            <label>Title</label>
            <input
              type="text"
              value={editingProject.title}
              onChange={(e) => setEditingProject({...editingProject, title: e.target.value})}
              placeholder="Project Title"
            />
          </FormGroup>
          
          <FormGroup>
            <label>Description</label>
            <textarea
              value={editingProject.description}
              onChange={(e) => setEditingProject({...editingProject, description: e.target.value})}
              placeholder="Project Description"
              rows="4"
            />
          </FormGroup>
          
          <FormRow>
            <FormGroup>
              <label>GitHub Link</label>
              <input
                type="text"
                value={editingProject.github}
                onChange={(e) => setEditingProject({...editingProject, github: e.target.value})}
                placeholder="GitHub URL"
              />
            </FormGroup>
            
            <FormGroup>
              <label>Demo Link</label>
              <input
                type="text"
                value={editingProject.demo}
                onChange={(e) => setEditingProject({...editingProject, demo: e.target.value})}
                placeholder="Demo URL"
              />
            </FormGroup>
          </FormRow>
          
          <FormGroup>
            <label>Category</label>
            <select 
              value={editingProject.category}
              onChange={(e) => setEditingProject({...editingProject, category: e.target.value})}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </FormGroup>
          
          <FormGroup>
            <label>Technologies</label>
            <TechContainer>
              {commonTechs.map(tech => (
                <TechBadge 
                  key={tech} 
                  $active={editingProject.technologies.includes(tech)}
                  onClick={() => handleTechnologyToggle(tech, editingProject)}
                >
                  {tech}
                </TechBadge>
              ))}
            </TechContainer>
          </FormGroup>
          
          <FormGroup>
            <label>Project Image</label>
            <ProjectImagePreview>
              {editingProject.image && <img src={editingProject.image} alt="Project preview" />}
            </ProjectImagePreview>
            <ImageUploader onImageUpload={(url) => handleImageUpload(url, editingProject)} />
          </FormGroup>
          
          <ButtonGroup>
            <SaveButton onClick={handleUpdateProject}>Update Project</SaveButton>
            <CancelButton onClick={() => setEditingProject(null)}>Cancel</CancelButton>
          </ButtonGroup>
        </FormContainer>
      )}

      <ProjectsList>
        {projects.map(project => (
          <ProjectItem key={project.id}>
            <ProjectInfo>
              <ProjectImageThumb src={project.image} alt={project.title} />
              <ProjectDetails>
                <h3>{project.title}</h3>
                <p>{project.description.substring(0, 100)}...</p>
                <ProjectMeta>
                  <span>Category: {project.category}</span>
                  <TechList>
                    {project.technologies.map((tech, index) => (
                      <small key={index}>{tech}{index < project.technologies.length - 1 ? ', ' : ''}</small>
                    ))}
                  </TechList>
                </ProjectMeta>
              </ProjectDetails>
            </ProjectInfo>
            <ProjectActions>
              <EditButton onClick={() => handleEditProject(project)}>
                <FaEdit /> Edit
              </EditButton>
              <DeleteButton onClick={() => handleDeleteProject(project.id)}>
                <FaTrash /> Delete
              </DeleteButton>
            </ProjectActions>
          </ProjectItem>
        ))}
      </ProjectsList>

      <SaveAllButton onClick={handleSaveProjects}>Save All Changes</SaveAllButton>
    </Container>
  );
};

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    color: var(--text-primary);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(100, 255, 218, 0.1);
  color: var(--accent);
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
`;

const FormContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  
  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    color: var(--text-secondary);
    font-weight: 500;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 10px;
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    color: var(--text-primary);
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: var(--accent);
    }
  }
  
  select {
    appearance: none;
    background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23CCCCCC%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
    background-repeat: no-repeat;
    background-position: right 10px top 50%;
    background-size: 12px auto;
    padding-right: 30px;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  
  ${FormGroup} {
    flex: 1;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const TechContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const TechBadge = styled.div`
  padding: 6px 12px;
  background-color: ${props => props.$active ? 'var(--accent)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$active ? 'var(--secondary)' : 'var(--text-primary)'};
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.$active ? 'var(--accent)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const ProjectImagePreview = styled.div`
  width: 100%;
  height: 200px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: transparent;
  color: var(--text-primary);
  border: 1px solid var(--text-secondary);
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const SaveAllButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px;
  margin-top: 20px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const ProjectsList = styled.div`
  margin-top: 30px;
`;

const ProjectItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ProjectInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 15px;
  }
`;

const ProjectImageThumb = styled.img`
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 15px;
`;

const ProjectDetails = styled.div`
  h3 {
    margin: 0 0 5px;
    color: var(--text-primary);
    font-size: 1.1rem;
  }
  
  p {
    margin: 0 0 5px;
    color: var(--text-secondary);
    font-size: 0.9rem;
  }
`;

const ProjectMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  
  span {
    font-size: 0.85rem;
    color: var(--accent);
  }
`;

const TechList = styled.div`
  small {
    color: var(--text-secondary);
    font-size: 0.85rem;
  }
`;

const ProjectActions = styled.div`
  display: flex;
  gap: 10px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background-color: transparent;
  color: var(--accent);
  border: 1px solid var(--accent);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background-color: transparent;
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 107, 107, 0.1);
  }
`;

export default ProjectsAdmin;